// @flow

import React, { useState } from 'react';
import styled from '@emotion/styled';
import { get, without } from 'lodash/fp';

import LineUpHeader from 'app/components/challenge/LineUpHeader';
import LineUpSection from 'app/components/challenge/LineUpSection';
import LineUpTable from 'app/components/challenge/LineUpTable';
import UserStatsCols from 'app/components/challenge/UserStatsCols';
import Rows from 'app/components/layout/Rows';
import Icon from '../../components/commons/Icon';

type Props = {
  isDesktop: boolean,
  isLive?: boolean,
  lineUps: Array<*>,
  searchTerm?: string,
  showProjectedScore?: boolean,
  renderEntity: (entity: Object, positionId: string) => React.Node,
  renderBottomButtons?: (selectionHash: string, lineupId: number) => void,
  toggleLineupFavorite?: (id: number, favorite: boolean) => void
};

const defaultProps = {
  isLive: false,
  showProjectedScore: false,
  renderBottomButtons: undefined,
  searchTerm: undefined,
  toggleLineupFavorite: undefined
};

const LineUpWrapper = styled.div(
  {
    display: 'flex',
    flexDirection: 'column'
  },
  ({ theme }) => ({
    paddingBottom: theme.spacing.small
  })
);

const TableWrapper = styled.div(({ theme }) => ({
  flex: '0 1 auto',
  overflowX: 'hidden',
  backgroundColor: theme.color.white
}));

const StyledUserStatsCols = styled(UserStatsCols)(({ theme }) => ({
  backgroundColor: theme.color.primary,
  borderBottom: `1px solid ${theme.color.primary}`
}));

const DropDownWrapper = styled.div(({ theme }) => ({
  backgroundColor: theme.color.basis3,
  color: theme.color.white,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  outline: 'none',
  paddingBottom: '0.4em',
  width: '100%'
}));

const StyledIcon = styled(Icon)(
  {
    transition: 'all 0.2s ease-out'
  },
  ({ isUp }) =>
    isUp
      ? {
          transform: 'rotate(270deg)'
        }
      : {
          transform: 'rotate(90deg)'
        }
);

const HeaderButton = styled.button({
  padding: 0,
  outline: 'none'
});

const LineUpGrid: React.FC<Props> = ({
  isDesktop,
  isLive,
  lineUps,
  renderEntity,
  renderBottomButtons,
  searchTerm,
  showProjectedScore,
  toggleLineupFavorite
}: Props) => {
  const [expandedTeams, setExpandedTeams] = useState([]);

  const toggleDropDown = selectionHash =>
    setExpandedTeams(
      expandedTeams.includes(selectionHash)
        ? without([selectionHash], expandedTeams)
        : [...expandedTeams, selectionHash]
    );

  return (
    <Rows mobile={1} desktop={3}>
      {lineUps.map(
        ({
          entryCount,
          firstChallenge,
          id: lineupId,
          isRacing,
          favorite,
          name,
          rank,
          remainingSalary,
          score,
          selection,
          selectionHash,
          sportIcon,
          startTime,
          statusId,
          statusLabel,
          totalEntries,
          totalPrize,
          projectedScore
        }) => (
          <LineUpWrapper key={lineupId}>
            <LineUpHeader
              id={lineupId}
              favorite={favorite}
              isLive={isLive}
              name={name}
              numEntries={entryCount}
              remainingSalary={remainingSalary}
              sportIcon={sportIcon}
              startTime={startTime}
              statusId={statusId}
              statusLabel={statusLabel}
              onToggleFavorite={toggleLineupFavorite}
              projectedScore={showProjectedScore ? projectedScore : undefined}
            />
            {!isDesktop && (
              <HeaderButton
                onClick={() => {
                  toggleDropDown(selectionHash);
                }}
              >
                <DropDownWrapper>
                  <StyledIcon
                    type="icon-right-arrow"
                    isUp={expandedTeams.includes(selectionHash)}
                  />
                </DropDownWrapper>
              </HeaderButton>
            )}
            {isLive && (
              <StyledUserStatsCols
                entryCount={totalEntries}
                prize={totalPrize}
                rank={rank}
                score={score}
              />
            )}
            <TableWrapper
              hidden={!searchTerm && !isDesktop && !expandedTeams.includes(selectionHash)}
            >
              <LineUpTable
                positions={selection}
                renderPosition={(pos, i) => (
                  <LineUpSection
                    key={`${pos.id}_${i}`}
                    abbr={pos.abbr || pos.id}
                    filled={pos.filled}
                    hasError={pos.hasError}
                    id={pos.id}
                    isRacing={isRacing}
                    name={pos.name}
                    selections={pos.entities}
                    renderEntity={(entity, ...restArgs) =>
                      renderEntity(
                        {
                          ...entity,
                          isRacing,
                          firstChallengeId: get('id', firstChallenge),
                          highlight:
                            searchTerm &&
                            entity.name.toLowerCase().indexOf(searchTerm.toLowerCase().trim()) !==
                              -1,
                          lineupId
                        },
                        ...restArgs
                      )
                    }
                  />
                )}
              />
            </TableWrapper>
            {renderBottomButtons && renderBottomButtons(selectionHash, lineupId, entryCount)}
          </LineUpWrapper>
        )
      )}
    </Rows>
  );
};

LineUpGrid.defaultProps = defaultProps;

export default LineUpGrid;
